import { IconOptionsList } from '@doltech/icons-from-dev/IconOptionsList';
import { Space } from 'antd';
import cl from 'classnames';
import { noop } from 'lodash';
import * as React from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet';
import styled from 'styled-components';
import { Cell, Grid } from 'styled-css-grid';

import { CircleText, QuestionCircle, QuestionDisplayMode } from '../../CircleText/CircleText';
import { colorsV2 } from '../../colors-v2';
import { Typography } from '../../DesignSystemV2/Typography';
import { Divider } from '../../Divider/Divider';
import { SubText } from '../../Typography';
import { DrawerBody } from '../Modal/DrawerModal';
import { MODEL_CLASS } from '../Modal/ModelConstant';

interface OptionsListDrawerProps {
  type?: string;
  options?: any[];
  onItemClick?: (item: string) => void;
  visible: boolean;
  onClose?: () => void;
  header?: any;
  numberToString?: any;
  selectedOpt: number[];
  title: string;
  qNumber?: number;
}

export const DrawerType = {
  OPTION_LIST: 'option-list',
  OPTION_LIST_NO_TEXT: 'option-list-no-text',
  TEXT_ONLY: 'option-list-text-only',
} as const;

export type DrawerType = (typeof DrawerType)[keyof typeof DrawerType];

const GridStyled = styled(Grid)`
  box-sizing: border-box;
  border-radius: 8px;
  height: auto;
  padding: 8px 10px;
`;

const GridNoText = styled(Grid)`
  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const GridNoTextItem = styled(Cell)`
  width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  padding-top: 100%;
  display: flex;
  justify-content: center;
  position: relative;
  background-color: ${colorsV2.gray5};
  border: 1px dashed ${colorsV2.gray20};

  .question-circle {
    position: absolute;
    right: 0;
    left: 0;
    top: 0;
    bottom: 0;
    margin: auto;
  }
  &:active {
    border: 0;
  }
`;

const CellCenter = styled(Cell)`
  height: 48px;
  display: flex;
  flex-direction: column;
`;

const DrawerTitle = React.memo(
  ({ title, qNumber, type }: { title: string; qNumber?: number; type?: string }) => {
    return (
      <Grid rows={1} columns="auto max-content" gap="0">
        <CellCenter>
          <Typography.Text variant="semi-bold/20">{title}</Typography.Text>
          {qNumber && <SubText>Chọn đáp án cho câu số {qNumber}</SubText>}
        </CellCenter>
        {type !== DrawerType.TEXT_ONLY && (
          <Cell>
            <IconOptionsList />
          </Cell>
        )}
      </Grid>
    );
  }
);

const StyledBottomSheet = styled(BottomSheet)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: grid;
  align-items: flex-end;
`;

const BottomSheetContent = styled.div`
  padding: 0 16px 80px;
`;

const OptionListContent = React.memo(({ type, options, onItemClick, numberToString }: any) => {
  if (DrawerType.OPTION_LIST_NO_TEXT === type) {
    return (
      <GridNoText columns="repeat(3, minmax(0, 1fr))" gap="16px">
        {options.map((item) => (
          <GridNoTextItem
            key={item.key}
            onClick={(e) => {
              e.preventDefault();
              onItemClick(item);
            }}
          >
            <QuestionCircle
              className={cl('question-circle', QuestionDisplayMode.QUESTION_OPTION_DRAG, {
                selected: true,
                'shape-circle': true,
              })}
            >
              <SubText>{numberToString(item.key + 1)}</SubText>
            </QuestionCircle>
          </GridNoTextItem>
        ))}
      </GridNoText>
    );
  }

  if (DrawerType.TEXT_ONLY === type) {
    return (
      <Space
        style={{ width: '100%' }}
        direction="vertical"
        split={<Divider.Line space={0} directional="horizontal" />}
      >
        {options.map((item) => (
          <Typography.Paragraph
            style={{ cursor: 'pointer' }}
            variant="medium/14"
            key={item.key}
            onClick={(e) => {
              e.preventDefault();
              onItemClick(item);
            }}
          >
            {item.text ? item.text : item.value}
          </Typography.Paragraph>
        ))}
      </Space>
    );
  }

  // DrawerType.OPTION_LIST is default.
  return options.map((item) => (
    <GridStyled
      columns="minmax(0, 1fr)"
      key={item.key}
      onClick={(e) => {
        e.preventDefault();
        onItemClick(item);
      }}
    >
      <Cell>
        <CircleText
          displayMode={QuestionDisplayMode.QUESTION_OPTION_DRAG}
          circleText={numberToString(item.key + 1)}
          text={item.text ? item.text : item.value}
        />
      </Cell>
    </GridStyled>
  ));
});

export const OptionsListDrawer = ({
  type = DrawerType.OPTION_LIST,
  options,
  numberToString,
  onItemClick = noop,
  visible,
  onClose,
  header,
  selectedOpt = [],
  title,
  qNumber,
}: OptionsListDrawerProps) => {
  const hasHeader = Boolean(header);

  return (
    <StyledBottomSheet
      className={MODEL_CLASS}
      open={visible}
      onDismiss={onClose}
      snapPoints={({ minHeight, maxHeight }) => [
        Math.min(minHeight, maxHeight - 72),
        maxHeight - 72,
      ]}
      initialFocusRef={false}
      skipInitialTransition
    >
      <BottomSheetContent>
        {hasHeader ? header : <DrawerTitle type={type} title={title} qNumber={qNumber} />}
        <DrawerBody footer={false}>
          <OptionListContent
            type={type}
            options={options}
            selectedOpt={selectedOpt}
            onItemClick={onItemClick}
            numberToString={numberToString}
          />
        </DrawerBody>
      </BottomSheetContent>
    </StyledBottomSheet>
  );
};
